import type {
    ScopeFilterConfig,
    ScopeFilterOption,
    ScopeFilterResolutionContext,
    ScopeFilterType,
} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDataViewerConfig";
import type {FilterConfiguration, FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

export function getScopeFilterDrawerTitle(config: ScopeFilterConfig): string {
    if (config.drawerTitle !== undefined && config.drawerTitle.trim() !== "") {
        return config.drawerTitle;
    }
    return "Type";
}

export function getScopeFilterSelectionMode(config: ScopeFilterConfig): "single" | "multi" {
    if (config.selectionMode === "multi") {
        return "multi";
    }
    return "single";
}

export function getScopeFilterOptionFilterType(option: ScopeFilterOption): ScopeFilterType {
    if (option.filterType === "in") {
        return "in";
    }
    return "equals";
}

export function getScopeFilterKeys(config: ScopeFilterConfig): Set<string> {
    return new Set(config.options.map((option) => option.filterKey));
}

function scopeOptionResolvedValue(
    option: ScopeFilterOption,
    resolutionContext: ScopeFilterResolutionContext | undefined,
): unknown {
    if (option.type === "User" && resolutionContext !== undefined) {
        return resolutionContext.workspaceUserId;
    }
    if (option.defaultValue !== undefined) {
        return option.defaultValue;
    }
    return true;
}

function stringResolvedValue(
    option: ScopeFilterOption,
    resolutionContext: ScopeFilterResolutionContext | undefined,
): string {
    return String(scopeOptionResolvedValue(option, resolutionContext));
}

function lineItemMatchesScopeOption(
    lineItem: FilterConfigurationLineItem,
    option: ScopeFilterOption,
    resolutionContext: ScopeFilterResolutionContext | undefined,
): boolean {
    if (lineItem.columnId !== option.filterKey) {
        return false;
    }
    if (lineItem.operation === FilterOperation.equals) {
        const expected = scopeOptionResolvedValue(option, resolutionContext);
        const actual = lineItem.value;
        if (typeof expected === "boolean" || typeof actual === "boolean") {
            return Boolean(expected) === Boolean(actual);
        }
        return String(actual) === String(expected);
    }
    if (lineItem.operation === FilterOperation.in) {
        return lineItem.values.includes(stringResolvedValue(option, resolutionContext));
    }
    return false;
}

export function scopeOptionToLineItem(
    option: ScopeFilterOption,
    resolutionContext?: ScopeFilterResolutionContext,
): FilterConfigurationLineItem {
    return {
        columnId: option.filterKey,
        operation: FilterOperation.equals,
        value: scopeOptionResolvedValue(option, resolutionContext),
    };
}

function scopeSelectedOptionsToLineItems(
    selectedOptions: Array<ScopeFilterOption>,
    resolutionContext: ScopeFilterResolutionContext | undefined,
): Array<FilterConfigurationLineItem> {
    if (selectedOptions.length === 0) {
        return [];
    }

    const keyOrder: Array<string> = [];
    const byKey = new Map<string, Array<ScopeFilterOption>>();

    for (const option of selectedOptions) {
        const existing = byKey.get(option.filterKey);
        if (existing === undefined) {
            keyOrder.push(option.filterKey);
            byKey.set(option.filterKey, [option]);
        } else {
            existing.push(option);
        }
    }

    const lineItems: Array<FilterConfigurationLineItem> = [];

    for (const key of keyOrder) {
        const group = byKey.get(key);
        if (group === undefined) {
            continue;
        }
        if (group.length === 1) {
            const only = group[0];
            if (only !== undefined) {
                if (getScopeFilterOptionFilterType(only) === "in") {
                    lineItems.push({
                        columnId: key,
                        operation: FilterOperation.in,
                        values: [stringResolvedValue(only, resolutionContext)],
                    });
                } else {
                    lineItems.push(scopeOptionToLineItem(only, resolutionContext));
                }
            }
            continue;
        }
        if (group.every((opt) => getScopeFilterOptionFilterType(opt) === "in") === true) {
            lineItems.push({
                columnId: key,
                operation: FilterOperation.in,
                values: group.map((opt) => stringResolvedValue(opt, resolutionContext)),
            });
        } else {
            for (const opt of group) {
                lineItems.push(scopeOptionToLineItem(opt, resolutionContext));
            }
        }
    }

    return lineItems;
}

export function removeScopeFilters(
    filterConfiguration: FilterConfiguration,
    scopeKeys: Set<string>,
): FilterConfiguration {
    return {
        revisionId: generateUuid(),
        lineItems: filterConfiguration.lineItems.filter((lineItem) => scopeKeys.has(lineItem.columnId) === false),
    };
}

/**
 * Option ids currently reflected in filter line items (only options present in config).
 */
export function getSelectedScopeOptionIdsFromFilter(
    filterConfiguration: FilterConfiguration,
    config: ScopeFilterConfig,
    resolutionContext?: ScopeFilterResolutionContext,
): Set<Uuid> {
    const selected = new Set<Uuid>();
    for (const option of config.options) {
        const match = filterConfiguration.lineItems.some((lineItem) =>
            lineItemMatchesScopeOption(lineItem, option, resolutionContext),
        );
        if (match === true) {
            selected.add(option.id);
        }
    }
    return selected;
}

export function isScopeFilterAllState(filterConfiguration: FilterConfiguration, config: ScopeFilterConfig): boolean {
    const keys = getScopeFilterKeys(config);
    return filterConfiguration.lineItems.some((lineItem) => keys.has(lineItem.columnId)) === false;
}

export function applyScopeSelectionToFilterConfiguration(
    filterConfiguration: FilterConfiguration,
    config: ScopeFilterConfig,
    selectedOptionIds: Set<Uuid> | null,
    resolutionContext?: ScopeFilterResolutionContext,
): FilterConfiguration {
    const scopeKeys = getScopeFilterKeys(config);
    const withoutScope = removeScopeFilters(filterConfiguration, scopeKeys);

    if (selectedOptionIds === null || selectedOptionIds.size === 0) {
        return withoutScope;
    }

    const selectedOptions = config.options.filter((option) => selectedOptionIds.has(option.id));
    const newLineItems: Array<FilterConfigurationLineItem> = [
        ...withoutScope.lineItems,
        ...scopeSelectedOptionsToLineItems(selectedOptions, resolutionContext),
    ];

    return {
        revisionId: generateUuid(),
        lineItems: newLineItems,
    };
}
